import { Box, Button, Radio, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { LANGUAGES, type LanguageCode } from "~/utils/languages";
import { CenterModal } from "../CenterModal";
import { MODAL_FORM_LAYOUT } from "../modalFormStyles";

interface ChangeLanguageModalProps {
  open: boolean;
  onClose: () => void;
  currentLanguage: LanguageCode;
  onSave: (language: LanguageCode) => void;
}

export function ChangeLanguageModal({
  open,
  onClose,
  currentLanguage,
  onSave,
}: ChangeLanguageModalProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [selectedLang, setSelectedLang] = useState<LanguageCode>(currentLanguage);

  useEffect(() => {
    if (open) setSelectedLang(currentLanguage);
  }, [open, currentLanguage]);

  return (
    <CenterModal
      open={open}
      onClose={onClose}
      title={t("auth.changeLanguage")}
      size="md"
      width={480}
      headerSx={{ pb: "24px" }}
    >
      <Box>
        <Stack spacing={2}>
          {LANGUAGES.map((lang) => {
            const isSelected = selectedLang === lang.code;
            return (
              <Box
                key={lang.code}
                onClick={() => setSelectedLang(lang.code)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  minHeight: 52,
                  px: 2,
                  py: 1.5,
                  border: "1px solid",
                  borderColor: isSelected
                    ? theme.palette.custom.primary
                    : theme.palette.custom.gray[300],
                  borderRadius: "var(--Input-Radius-Medium)",
                  bgcolor: isSelected
                    ? theme.palette.custom.primarySecondaryPressed
                    : theme.palette.background.paper,
                  cursor: "pointer",
                  transition: "all 0.15s",
                  "&:hover": {
                    borderColor: theme.palette.custom.primary,
                    bgcolor: theme.palette.custom.primarySecondaryHover,
                  },
                  "&:active": {
                    bgcolor: theme.palette.custom.primarySecondaryPressed,
                  },
                }}
              >
                <Radio
                  checked={isSelected}
                  sx={{
                    p: 0,
                    color: theme.palette.custom.gray[300],
                    "&.Mui-checked": { color: theme.palette.custom.primary },
                  }}
                />
                <Typography
                  sx={{
                    fontSize: 16,
                    fontWeight: 500,
                    color: theme.palette.custom.gray[700],
                  }}
                >
                  {lang.label}
                </Typography>
              </Box>
            );
          })}
        </Stack>

        <Stack
          direction="row"
          justifyContent="flex-end"
          spacing={2}
          sx={{
            mt: "24px",
            pb: MODAL_FORM_LAYOUT.actionsPaddingBottom,
          }}
        >
          <Button
            variant="contained"
            onClick={() => onSave(selectedLang)}
            fullWidth
            sx={{ fontWeight: 600, px: 3, minHeight: "52px" }}
          >
            {t("auth.saveChange")}
          </Button>
        </Stack>
      </Box>
    </CenterModal>
  );
}
